'use client'

import { useSyncExternalStore } from 'react'

/**
 * Whether promoting the iOS app makes sense for this visitor.
 *
 * The app is iPhone-only — components/ui/app-download-cta.tsx resolves an App
 * Store URL and nothing else — so the question is really "can this device act
 * on an App Store link?":
 *
 *   - iOS: yes, the badge opens the App Store directly.
 *   - Desktop: yes, via the QR handoff AppDownloadCTA already renders behind
 *     `[@media(pointer:fine)]`.
 *   - Android: no. The link dead-ends on Apple's web preview, so an app-led
 *     wall would strand them with no way forward.
 *
 * So this detects ANDROID and hides, rather than detecting iOS and showing.
 * Anything unrecognized keeps the CTA — a false positive costs one irrelevant
 * card, a false negative silently removes the highest-intent install surface.
 *
 * useSyncExternalStore (not useEffect) so the server snapshot is explicit:
 * SSR renders the CTA, and an Android client corrects on hydration without a
 * mismatch warning. The subscribe function is a no-op because a user agent
 * cannot change within a page's lifetime.
 */

function subscribe(): () => void {
  return () => {}
}

function getSnapshot(): boolean {
  if (typeof navigator === 'undefined') return true
  return !/android/i.test(navigator.userAgent)
}

/** SSR + first client render: assume the CTA is worth showing. */
function getServerSnapshot(): boolean {
  return true
}

export function useAppInstallAvailable(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Direct user-agent read, for gates that are NOT render output.
 *
 * The hook deliberately returns `true` on the hydration render so the client
 * markup matches the server's, then corrects. That is right for what renders,
 * but wrong for impression enablement: during that one render an Android
 * device still looks eligible, so an observer registered in that window could
 * emit a `cta_surface_shown` for a device that can never produce the matching
 * click. That is precisely the denominator pollution the Android gate exists
 * to prevent, so impressions read the UA directly and skip the window.
 *
 * Never use this to decide markup — it would reintroduce the hydration
 * mismatch the hook is structured to avoid.
 */
export function appInstallAvailableNow(): boolean {
  return getSnapshot()
}
